import type { FastifyRequest, FastifyReply } from 'fastify'
import type { Firestore } from 'firebase-admin/firestore'
import type { RedisClient } from '../../core/redis/client.js'
import { GamificationAdminService } from './gamification-admin.service'
import { ApiResponse } from '../../utils/response'
import { z, ZodError } from 'zod'
import type {
  CreateQuestTemplateDTO,
  CreateChallengeTemplateDTO,
  UpdateQuestTemplateDTO,
  UpdateChallengeTemplateDTO,
  CreateSeasonConfigDTO,
  UpdateSeasonConfigDTO,
  CreateBadgeDTO,
  UpdateBadgeDTO,
  BadgePreviewRequest
} from '@my-roadtrip/types'

/**
 * Gamification Admin Controller - Admin-only endpoints for managing gamification data
 * 
 * All routes require admin authentication
 */
export class GamificationAdminController {
  private readonly service: GamificationAdminService
  private static readonly badgeConceptSchema = z
    .object({
      preset: z.string().trim().min(2).max(80).optional(),
      prompt: z.string().trim().min(3).max(800).optional(),
      tone: z.string().trim().max(48).optional(),
      audience: z.string().trim().max(120).optional(),
      rarity: z.enum(['common', 'rare', 'epic', 'legendary']).optional(),
      locale: z.string().trim().max(10).optional(),
      style: z.string().trim().max(120).optional(),
      generateImage: z.coerce.boolean().optional(),
      iconVariant: z.enum(['flat', '3d', 'outlined']).optional()
    })
    .refine(
      (payload) => Boolean(payload.preset) || Boolean(payload.prompt),
      'preset or prompt is required to generate a concept'
    )
  private static readonly badgeIdSchema = z
    .string()
    .trim()
    .min(3)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9\-_]+$/i, 'Badge id must be alphanumeric and can include - or _')
  private static readonly createBadgeSchema = z.object({
    id: GamificationAdminController.badgeIdSchema,
    name: z.string().trim().min(3).max(80),
    description: z.string().trim().min(10).max(320),
    icon: z.string().trim().min(1).max(8),
    rarity: z.enum(['common', 'rare', 'epic', 'legendary']),
    requirement: z.string().trim().max(240).optional().nullable(),
    accentColor: z.string().trim().max(32).optional().nullable(),
    backgroundGradient: z.string().trim().max(160).optional().nullable(),
    iconUrl: z.string().trim().max(512).optional().nullable(),
    iconSource: z.enum(['emoji', 'vector', 'image']).optional(),
    category: z.string().trim().max(120).optional().nullable(),
    tags: z
      .array(z.string().trim().min(1).max(48))
      .max(20)
      .optional(),
    aiPrompt: z.string().trim().max(600).optional().nullable(),
    aiModel: z.string().trim().max(80).optional().nullable()
  })
  private static readonly updateBadgeSchema = GamificationAdminController.createBadgeSchema
    .omit({ id: true })
    .partial()
  private static readonly badgeAssignmentSchema = z.object({
    userId: z.string().trim().min(1),
    badgeId: GamificationAdminController.badgeIdSchema
  })

  constructor(firestore: Firestore, redis: RedisClient) {
    this.service = new GamificationAdminService(firestore, redis)
  }

  // ========== QUEST TEMPLATE ENDPOINTS ==========

  async createQuestTemplate(
    request: FastifyRequest<{ Body: CreateQuestTemplateDTO }>,
    reply: FastifyReply
  ) {
    try {
      const template = await this.service.createQuestTemplate(request.body)
      return reply.status(201).send(template)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to create quest template')
    }
  }

  async updateQuestTemplate(
    request: FastifyRequest<{
      Params: { templateId: string }
      Body: UpdateQuestTemplateDTO
    }>,
    reply: FastifyReply
  ) {
    try {
      const { templateId } = request.params
      const template = await this.service.updateQuestTemplate(templateId, request.body)
      return reply.send(template)
    } catch (error) {
      request.log.error(error)
      if (error instanceof Error && error.message === 'Quest template not found') {
        return ApiResponse.notFound(reply, 'Quest template not found')
      }
      return ApiResponse.internalError(reply, 'Failed to update quest template')
    }
  }

  async deleteQuestTemplate(
    request: FastifyRequest<{ Params: { templateId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const { templateId } = request.params
      await this.service.deleteQuestTemplate(templateId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      if (error instanceof Error && error.message === 'Quest template not found') {
        return ApiResponse.notFound(reply, 'Quest template not found')
      }
      return ApiResponse.internalError(reply, 'Failed to delete quest template')
    }
  }

  async getQuestTemplates(
    request: FastifyRequest<{ Querystring: { includeDisabled?: string } }>,
    reply: FastifyReply
  ) {
    try {
      const includeDisabled = request.query.includeDisabled === 'true'
      const templates = await this.service.getQuestTemplates(includeDisabled)
      return reply.send(templates)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get quest templates')
    }
  }

  async getQuestTemplateById(
    request: FastifyRequest<{ Params: { templateId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const { templateId } = request.params
      const template = await this.service.getQuestTemplateById(templateId)
      
      if (!template) {
        return ApiResponse.notFound(reply, 'Quest template not found')
      }

      return reply.send(template)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get quest template')
    }
  }

  // ========== CHALLENGE TEMPLATE ENDPOINTS ==========

  async createChallengeTemplate(
    request: FastifyRequest<{ Body: CreateChallengeTemplateDTO }>,
    reply: FastifyReply
  ) {
    try {
      const template = await this.service.createChallengeTemplate(request.body)
      return reply.status(201).send(template)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to create challenge template')
    }
  }

  async updateChallengeTemplate(
    request: FastifyRequest<{
      Params: { templateId: string }
      Body: UpdateChallengeTemplateDTO
    }>,
    reply: FastifyReply
  ) {
    try {
      const { templateId } = request.params
      const template = await this.service.updateChallengeTemplate(templateId, request.body)
      return reply.send(template)
    } catch (error) {
      request.log.error(error)
      if (error instanceof Error && error.message === 'Challenge template not found') {
        return ApiResponse.notFound(reply, 'Challenge template not found')
      }
      return ApiResponse.internalError(reply, 'Failed to update challenge template')
    }
  }

  async deleteChallengeTemplate(
    request: FastifyRequest<{ Params: { templateId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const { templateId } = request.params
      await this.service.deleteChallengeTemplate(templateId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      if (error instanceof Error && error.message === 'Challenge template not found') {
        return ApiResponse.notFound(reply, 'Challenge template not found')
      }
      return ApiResponse.internalError(reply, 'Failed to delete challenge template')
    }
  }

  async getChallengeTemplates(
    request: FastifyRequest<{ Querystring: { includeDisabled?: string } }>,
    reply: FastifyReply
  ) {
    try {
      const includeDisabled = request.query.includeDisabled === 'true'
      const templates = await this.service.getChallengeTemplates(includeDisabled)
      return reply.send(templates)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get challenge templates')
    }
  }

  async getChallengeTemplateById(
    request: FastifyRequest<{ Params: { templateId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const { templateId } = request.params
      const template = await this.service.getChallengeTemplateById(templateId)
      
      if (!template) {
        return ApiResponse.notFound(reply, 'Challenge template not found')
      }

      return reply.send(template)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get challenge template')
    }
  }

  // ========== SEASON CONFIG ENDPOINTS ==========

  async createSeasonConfig(
    request: FastifyRequest<{ Body: CreateSeasonConfigDTO }>,
    reply: FastifyReply
  ) {
    try {
      const config = await this.service.createSeasonConfig(request.body)
      return reply.status(201).send(config)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to create season config')
    }
  }

  async updateSeasonConfig(
    request: FastifyRequest<{
      Params: { configId: string }
      Body: UpdateSeasonConfigDTO
    }>,
    reply: FastifyReply
  ) {
    try {
      const { configId } = request.params
      const config = await this.service.updateSeasonConfig(configId, request.body)
      return reply.send(config)
    } catch (error) {
      request.log.error(error)
      if (error instanceof Error && error.message === 'Season config not found') {
        return ApiResponse.notFound(reply, 'Season config not found')
      }
      return ApiResponse.internalError(reply, 'Failed to update season config')
    }
  }

  async deleteSeasonConfig(
    request: FastifyRequest<{ Params: { configId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const { configId } = request.params
      await this.service.deleteSeasonConfig(configId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      if (error instanceof Error && error.message === 'Season config not found') {
        return ApiResponse.notFound(reply, 'Season config not found')
      }
      return ApiResponse.internalError(reply, 'Failed to delete season config')
    }
  }

  async getSeasonConfigs(request: FastifyRequest, reply: FastifyReply) {
    try {
      const configs = await this.service.getSeasonConfigs()
      return reply.send(configs)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get season configs')
    }
  }

  async getActiveSeasonConfig(request: FastifyRequest, reply: FastifyReply) {
    try {
      const config = await this.service.getActiveSeasonConfig()
      
      if (!config) {
        return ApiResponse.notFound(reply, 'No active season found')
      }

      return reply.send(config)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get active season')
    }
  }

  // ========== BADGE MANAGEMENT ENDPOINTS ==========

  async generateBadgeConcept(
    request: FastifyRequest<{ Body: BadgePreviewRequest }>,
    reply: FastifyReply
  ) {
    try {
      const payload = GamificationAdminController.badgeConceptSchema.parse(request.body ?? {})
      const result = await this.service.generateBadgeConcept(payload)
      return reply.send(result)
    } catch (error) {
      request.log.error(error)
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, `Invalid badge concept request: ${JSON.stringify(error.flatten().fieldErrors)}`)
      }
      return ApiResponse.internalError(reply, 'Failed to generate badge concept')
    }
  }

  async createBadge(
    request: FastifyRequest<{ Body: CreateBadgeDTO }>,
    reply: FastifyReply
  ) {
    try {
      const payload = GamificationAdminController.createBadgeSchema.parse(request.body ?? {})
      const badge = await this.service.createBadge({
        ...payload,
        tags: payload.tags,
        createdBy: request.user?.uid ?? null
      })
      return reply.status(201).send(badge)
    } catch (error) {
      request.log.error(error)
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, `Invalid badge payload: ${JSON.stringify(error.flatten().fieldErrors)}`)
      }
      return ApiResponse.internalError(reply, 'Failed to create badge')
    }
  }

  async updateBadge(
    request: FastifyRequest<{ Params: { badgeId: string }; Body: UpdateBadgeDTO }>,
    reply: FastifyReply
  ) {
    try {
      const { badgeId } = request.params
      const payload = GamificationAdminController.updateBadgeSchema.parse(request.body ?? {})
      const badge = await this.service.updateBadge(badgeId, {
        ...payload,
        tags: payload.tags,
        requirement: payload.requirement || undefined,
        updatedBy: request.user?.uid ?? null
      })
      return reply.send(badge)
    } catch (error) {
      request.log.error(error)
      if (error instanceof Error && error.message === 'Badge not found') {
        return ApiResponse.notFound(reply, 'Badge not found')
      }
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, `Invalid badge update payload: ${JSON.stringify(error.flatten().fieldErrors)}`)
      }
      return ApiResponse.internalError(reply, 'Failed to update badge')
    }
  }

  async deleteBadge(
    request: FastifyRequest<{ Params: { badgeId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const { badgeId } = request.params
      await this.service.deleteBadge(badgeId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      if (error instanceof Error && error.message === 'Badge not found') {
        return ApiResponse.notFound(reply, 'Badge not found')
      }
      return ApiResponse.internalError(reply, 'Failed to delete badge')
    }
  }

  async getAllBadges(request: FastifyRequest, reply: FastifyReply) {
    try {
      const badges = await this.service.getAllBadges()
      return reply.send(badges)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get badges')
    }
  }

  async assignBadgeToUser(
    request: FastifyRequest<{
      Body: { userId: string; badgeId: string }
    }>,
    reply: FastifyReply
  ) {
    try {
      const payload = GamificationAdminController.badgeAssignmentSchema.parse(request.body ?? {})
      await this.service.assignBadgeToUser(payload.userId, payload.badgeId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      if (error instanceof Error) {
        if (error.message === 'User not found' || error.message === 'Badge not found') {
          return ApiResponse.notFound(reply, error.message)
        }
        if (error.message === 'User already has this badge') {
          return ApiResponse.badRequest(reply, error.message)
        }
      }
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, `Invalid badge assignment payload: ${JSON.stringify(error.flatten().fieldErrors)}`)
      }
      return ApiResponse.internalError(reply, 'Failed to assign badge')
    }
  }

  async removeBadgeFromUser(
    request: FastifyRequest<{
      Body: { userId: string; badgeId: string }
    }>,
    reply: FastifyReply
  ) {
    try {
      const payload = GamificationAdminController.badgeAssignmentSchema.parse(request.body ?? {})
      await this.service.removeBadgeFromUser(payload.userId, payload.badgeId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      if (error instanceof Error && error.message === 'User not found') {
        return ApiResponse.notFound(reply, error.message)
      }
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, `Invalid badge removal payload: ${JSON.stringify(error.flatten().fieldErrors)}`)
      }
      return ApiResponse.internalError(reply, 'Failed to remove badge')
    }
  }

  // ========== BULK OPERATIONS ==========

  async importQuestTemplates(
    request: FastifyRequest<{ Body: { templates: CreateQuestTemplateDTO[] } }>,
    reply: FastifyReply
  ) {
    try {
      const templates = await this.service.importQuestTemplates(request.body.templates)
      return reply.status(201).send({ imported: templates.length, templates })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to import quest templates')
    }
  }

  async importChallengeTemplates(
    request: FastifyRequest<{ Body: { templates: CreateChallengeTemplateDTO[] } }>,
    reply: FastifyReply
  ) {
    try {
      const templates = await this.service.importChallengeTemplates(request.body.templates)
      return reply.status(201).send({ imported: templates.length, templates })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to import challenge templates')
    }
  }

  async exportAllTemplates(request: FastifyRequest, reply: FastifyReply) {
    try {
      const data = await this.service.exportAllTemplates()
      return reply.send(data)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to export templates')
    }
  }

  // ========== USER QUEST/CHALLENGE MANAGEMENT ==========

  async forceCompleteQuest(
    request: FastifyRequest<{ Params: { questId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const { questId } = request.params
      await this.service.forceCompleteQuest(questId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      if (error instanceof Error && error.message === 'Quest not found') {
        return ApiResponse.notFound(reply, 'Quest not found')
      }
      return ApiResponse.internalError(reply, 'Failed to complete quest')
    }
  }

  async forceCompleteChallenge(
    request: FastifyRequest<{ Params: { challengeId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const { challengeId } = request.params
      await this.service.forceCompleteChallenge(challengeId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      if (error instanceof Error && error.message === 'Challenge not found') {
        return ApiResponse.notFound(reply, 'Challenge not found')
      }
      return ApiResponse.internalError(reply, 'Failed to complete challenge')
    }
  }

  async deleteUserQuest(
    request: FastifyRequest<{ Params: { questId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const { questId } = request.params
      await this.service.deleteUserQuest(questId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      if (error instanceof Error && error.message === 'Quest not found') {
        return ApiResponse.notFound(reply, 'Quest not found')
      }
      return ApiResponse.internalError(reply, 'Failed to delete quest')
    }
  }

  async deleteUserChallenge(
    request: FastifyRequest<{ Params: { challengeId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const { challengeId } = request.params
      await this.service.deleteUserChallenge(challengeId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      if (error instanceof Error && error.message === 'Challenge not found') {
        return ApiResponse.notFound(reply, 'Challenge not found')
      }
      return ApiResponse.internalError(reply, 'Failed to delete challenge')
    }
  }

  // ========== STATS & ANALYTICS ==========

  async getQuestStats(request: FastifyRequest, reply: FastifyReply) {
    try {
      const stats = await this.service.getQuestStats()
      return reply.send(stats)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get quest stats')
    }
  }

  async getChallengeStats(request: FastifyRequest, reply: FastifyReply) {
    try {
      const stats = await this.service.getChallengeStats()
      return reply.send(stats)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get challenge stats')
    }
  }
}
